import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Gauge, Zap, Cog, Wrench, ShieldCheck, Activity, ArrowUpRight, KeyRound } from "lucide-react";

const SERVICES = [
  {
    n: "01",
    slug: "stage-1-remap",
    title: "Stage 1 Remap",
    icon: Zap,
    desc:
      "OEM-safe custom map. Sharper throttle, richer mid-range and smoother torque delivery on stock hardware.",
    stats: ["+25–30% BHP", "+35% torque", "Fuel-efficient"],
  },
  {
    n: "02",
    slug: "stage-2-remap",
    title: "Stage 2 Remap",
    icon: Gauge,
    desc:
      "Bolt-on hardware map — decat, high-flow filter, upgraded intercooler. Calibrated live to your build on the road.",
    stats: ["+40–45% BHP", "+50% torque", "Track ready"],
  },
  {
    n: "03",
    slug: "stage-3-remap",
    title: "Stage 3 & Custom",
    icon: Cog,
    desc:
      "Hybrid turbos, forged internals, methanol. Full standalone calibration with rolling-road validation.",
    stats: ["+60–100% BHP", "Standalone ECU", "Bespoke tune"],
  },
  {
    n: "04",
    slug: "dpf-egr-removal",
    title: "DPF / EGR / AdBlue",
    icon: ShieldCheck,
    desc:
      "Diagnostics-first approach — we solve the underlying fault before we ever touch software. Deletes carried out at owner's discretion.",
    stats: ["Fault-first", "Owner's discretion", "Diesel specialist"],
  },
  {
    n: "05",
    slug: "dpf-cleaning",
    title: "DPF Cleaning",
    icon: Wrench,
    desc:
      "DPF light or limp mode? Mobile diagnostics, forced regeneration and on-vehicle clean — road-legal, filter kept, same/next day.",
    stats: ["From £120", "Same-day slots", "Filter kept"],
  },
  {
    n: "06",
    slug: "gearbox-tuning",
    title: "Gearbox Tuning",
    icon: Activity,
    desc:
      "DSG / DCT / ZF 8HP TCU calibration. Faster shifts, higher torque limits, launch control unlocked.",
    stats: ["Sub-100ms shifts", "Launch mode", "Torque limit +"],
  },
  {
    n: "07",
    slug: "diagnostics",
    title: "Diagnostics",
    icon: Wrench,
    desc:
      "Deep OBD, live-data logging, and pre-purchase inspection. Autologic, VCDS, ODIS and factory tools.",
    stats: ["OBD + live data", "Health report", "£45 flat"],
  },
  {
    n: "08",
    slug: "car-key-programming",
    title: "Car Keys & Fobs",
    icon: KeyRound,
    desc:
      "Spare keys, replacement remote fobs and transponder programming on your driveway — proof of ownership required, typically 40–60% under dealer prices.",
    stats: ["From £95", "Mobile service", "Dealer-grade tools"],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="relative bg-carbon py-24 md:py-32 px-6 md:px-10 border-t border-white/10"
      data-testid="services-section"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-volt">
              ◉ Services / 06
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
              Every tune is <br />
              <span className="text-white/40">custom-mapped</span> for
              <span className="text-volt"> your car.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-[1px] bg-white/10">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.76, 0, 0.24, 1] }}
                className="col-span-12 md:col-span-6 lg:col-span-3 bg-carbon p-8 group hover:bg-surface-1 transition-colors duration-500"
                data-testid={`service-card-${s.n}`}
              >
                <Link to={`/services/${s.slug}`} className="block" aria-label={`Read more about ${s.title}`}>
                  <div className="flex items-start justify-between mb-8">
                    <span className="font-mono text-xs text-white/40">/ {s.n}</span>
                    <Icon className="w-6 h-6 text-white/60 group-hover:text-volt transition-colors duration-300" strokeWidth={1.25} />
                  </div>
                  <h3 className="font-display uppercase text-2xl md:text-3xl leading-tight mb-4 tracking-tight group-hover:text-volt transition-colors">
                    {s.title}
                  </h3>
                  <p className="font-body text-white/60 text-sm leading-relaxed mb-8">
                    {s.desc}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-widest text-white/50 mb-6">
                    {s.stats.map((st) => (
                      <span key={st}>◆ {st}</span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-volt">
                    Learn more <ArrowUpRight className="w-3 h-3" />
                  </span>
                </Link>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
